import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  updateDoc,
  collection,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Building2,
  FileText,
  Users,
  ShoppingBag,
  ExternalLink,
  Download,
  Eye,
  EyeOff,
  ShieldCheck,
  Edit3,
  X,
  FileCheck,
  MapPin,
  Phone,
  Search,
  Check,
  Ban,
  Activity,
  ChevronDown,
} from "lucide-react";
import "./Buyer360.css";
import "./Seller360.css";

/* ── Helpers ─────────────────────────────────────────────────── */
const fmt = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "₹0";
  const num = Number(n);
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const fmtShort = (n) => {
  if (!n || n === 0) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

const safeNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

const formatDate = (val) => {
  if (!val) return "—";
  let d;
  if (typeof val === "object" && val.seconds) {
    d = new Date(val.seconds * 1000);
  } else if (val?.toDate) {
    d = val.toDate();
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const GRADIENT_POOL = [
  "linear-gradient(135deg,#8a6b32,#c4973e)",
  "linear-gradient(135deg,#1d4ed8,#3b82f6)",
  "linear-gradient(135deg,#065f46,#10b981)",
  "linear-gradient(135deg,#7c3aed,#a78bfa)",
  "linear-gradient(135deg,#b45309,#f59e0b)",
  "linear-gradient(135deg,#dc2626,#f87171)",
];
const getGradient = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return GRADIENT_POOL[Math.abs(h) % GRADIENT_POOL.length];
};

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "dosrgvlaf";



/* ------------------------------------------------------------------
   Build primary asset URL from publicId or stored Cloudinary URL.
   If storedUrl is provided, return it directly because it is the
   exact valid URL returned by the Cloudinary upload API.
   ------------------------------------------------------------------ */
const buildCloudinaryUrl = (publicId, storedUrl = "") => {
  if (storedUrl && (storedUrl.startsWith("http://") || storedUrl.startsWith("https://"))) {
    return storedUrl;
  }
  if (!publicId || typeof publicId !== "string") return "";
  if (publicId.startsWith("http://") || publicId.startsWith("https://")) {
    return publicId;
  }
  return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${publicId}`;
};

/* ------------------------------------------------------------------
   Download URL: uses Cloudinary fl_attachment to force download.
   ------------------------------------------------------------------ */
const buildCloudinaryDownloadUrl = (publicId, storedUrl = "") => {
  const url = storedUrl || (publicId ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${publicId}` : "");
  if (!url) return "";
  if (url.includes("/image/upload/")) {
    return url.replace("/image/upload/", "/image/upload/fl_attachment/");
  }
  return url;
};

/* ------------------------------------------------------------------
   PDF string detector (checks public IDs, extensions, paths)
   ------------------------------------------------------------------ */
const isPdfString = (str = "") => {
  if (!str || typeof str !== "string") return false;
  const lower = str.toLowerCase();
  return lower.endsWith(".pdf") || lower.includes(".pdf.") || lower.includes(".pdf/") || lower.includes("/raw/upload/") || lower.includes("format=pdf");
};

/* PDF high-resolution rendered image (page 1) for modal viewer */
const getPdfPageRenderUrl = (publicId, storedUrl = "") => {
  const url = storedUrl || (publicId ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${publicId}` : "");
  if (!url) return null;
  if (url.includes("/image/upload/")) {
    return url.replace("/image/upload/", "/image/upload/pg_1,f_png,w_1200,c_limit/");
  }
  return url;
};

/* Thumbnail URL for document cards */
const getThumbnailUrl = (publicId, storedUrl = "", isPdf = false) => {
  const url = storedUrl || (publicId ? `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/${publicId}` : "");
  if (!url) return null;
  if (isPdf) {
    if (url.includes("/image/upload/")) {
      return url.replace("/image/upload/", "/image/upload/pg_1,f_png,w_400,c_limit/");
    }
    return null;
  }
  return url;
};

/* Google Docs Viewer for PDF embed */
const getPdfEmbedUrl = (rawUrl = "") => {
  if (!rawUrl) return "";
  return `https://docs.google.com/viewer?url=${encodeURIComponent(rawUrl)}&embedded=true`;
};

/* ── Main Component ─────────────────────────────────────────── */
const Buyer360 = () => {
  const { buyerId } = useParams();
  const navigate = useNavigate();

  // Active Tab
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [buyerDoc, setBuyerDoc] = useState(null);
  const [regDoc, setRegDoc] = useState(null);
  const [orders, setOrders] = useState([]);
  const [settledOrders, setSettledOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState(null);

  // Orders Tab filters
  const [orderSubTab, setOrderSubTab] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Modals & View Modes
  const [previewDoc, setPreviewDoc] = useState(null);
  const [pdfViewMode, setPdfViewMode] = useState("rendered"); // "rendered" | "native" | "gdocs"
  const [showPasskey, setShowPasskey] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [statusReason, setStatusReason] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  /* ── 1. Listen to buyer doc (with fallback query) ── */
  useEffect(() => {
    if (!buyerId) return;
    const unsub = onSnapshot(
      doc(db, "buyers", buyerId),
      async (snap) => {
        if (snap.exists()) {
          setBuyerDoc({ id: snap.id, ...snap.data() });
        } else {
          try {
            const q = query(
              collection(db, "buyers"),
              where("buyerId", "==", buyerId)
            );
            const qSnap = await getDocs(q);
            if (!qSnap.empty) {
              setBuyerDoc({ id: qSnap.docs[0].id, ...qSnap.docs[0].data() });
            } else {
              setBuyerDoc({ id: buyerId, notFound: true });
            }
          } catch (e) {
            setBuyerDoc({ id: buyerId, notFound: true });
          }
        }
      },
      (err) => {
        console.error("[Buyer360] buyer doc error:", err);
        setError(err.message);
      }
    );
    return () => unsub();
  }, [buyerId]);

  /* ── 2. Listen to registration doc (with multi-field fallback query) ── */
  useEffect(() => {
    if (!buyerId) return;
    const unsub = onSnapshot(
      doc(db, "buyerregistrations", buyerId),
      async (snap) => {
        if (snap.exists()) {
          setRegDoc({ id: snap.id, ...snap.data() });
          setLoading(false);
        } else {
          try {
            // 1. Query buyerregistrations by buyerId
            const q1 = query(
              collection(db, "buyerregistrations"),
              where("buyerId", "==", buyerId)
            );
            const snap1 = await getDocs(q1);
            if (!snap1.empty) {
              setRegDoc({ id: snap1.docs[0].id, ...snap1.docs[0].data() });
              setLoading(false);
              return;
            }

            // 2. Query buyerregistrations by contact
            const q2 = query(
              collection(db, "buyerregistrations"),
              where("organizationContact", "==", buyerId)
            );
            const snap2 = await getDocs(q2);
            if (!snap2.empty) {
              setRegDoc({ id: snap2.docs[0].id, ...snap2.docs[0].data() });
              setLoading(false);
              return;
            }

            // 3. Query sellerregistrations by buyerId (shared registration flow)
            const q3 = query(
              collection(db, "sellerregistrations"),
              where("buyerId", "==", buyerId)
            );
            const snap3 = await getDocs(q3);
            if (!snap3.empty) {
              setRegDoc({ id: snap3.docs[0].id, ...snap3.docs[0].data() });
              setLoading(false);
              return;
            }

            // 4. Query sellerregistrations by contact
            const q4 = query(
              collection(db, "sellerregistrations"),
              where("organizationContact", "==", buyerId)
            );
            const snap4 = await getDocs(q4);
            if (!snap4.empty) {
              setRegDoc({ id: snap4.docs[0].id, ...snap4.docs[0].data() });
              setLoading(false);
              return;
            }

            // 5. Direct doc lookup in sellerregistrations
            const sSnap = await getDoc(doc(db, "sellerregistrations", buyerId));
            if (sSnap.exists()) {
              setRegDoc({ id: sSnap.id, ...sSnap.data() });
              setLoading(false);
              return;
            }

            setRegDoc(null);
            setLoading(false);
          } catch (e) {
            console.error("[Buyer360] registration fallback error:", e);
            setRegDoc(null);
            setLoading(false);
          }
        }
      },
      (err) => {
        console.error("[Buyer360] registration doc error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [buyerId]);

  /* ── 2b. Secondary query using buyerDoc phone/mobile if regDoc not yet found ── */
  useEffect(() => {
    if (!regDoc && buyerDoc && !buyerDoc.notFound) {
      const phone = buyerDoc.mobile || buyerDoc.phoneNumber || buyerDoc.contactNumber;
      if (phone) {
        (async () => {
          try {
            const q1 = query(collection(db, "buyerregistrations"), where("organizationContact", "==", phone));
            const s1 = await getDocs(q1);
            if (!s1.empty) {
              setRegDoc({ id: s1.docs[0].id, ...s1.docs[0].data() });
              return;
            }
            const q2 = query(collection(db, "sellerregistrations"), where("organizationContact", "==", phone));
            const s2 = await getDocs(q2);
            if (!s2.empty) {
              setRegDoc({ id: s2.docs[0].id, ...s2.docs[0].data() });
            }
          } catch (e) {
            // ignore
          }
        })();
      }
    }
  }, [buyerDoc, regDoc]);

  /* ── 3. Fetch Orders (orderList) ── */
  useEffect(() => {
    if (!buyerId) return;
    setOrdersLoading(true);

    const q1 = query(
      collection(db, "orderList"),
      where("buyerId", "==", buyerId)
    );

    const unsub1 = onSnapshot(
      q1,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          source: "orderList",
          ...d.data(),
        }));
        setOrders(list);
        setOrdersLoading(false);
      },
      (err) => {
        console.error("[Buyer360] orderList error:", err);
        setOrdersLoading(false);
      }
    );

    return () => unsub1();
  }, [buyerId]);

  /* ── 4. Fetch Settled / Order History (myorders) ── */
  useEffect(() => {
    if (!buyerId) return;

    const q2 = query(
      collection(db, "myorders"),
      where("buyerId", "==", buyerId)
    );

    const unsub2 = onSnapshot(
      q2,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          source: "myorders",
          ...d.data(),
        }));
        setSettledOrders(list);
      },
      (err) => {
        console.error("[Buyer360] myorders error:", err);
      }
    );

    return () => unsub2();
  }, [buyerId]);

  /* ── Derived Buyer Information ── */
  const buyerName = (
    regDoc?.organizationName ||
    buyerDoc?.organizationName ||
    buyerDoc?.name ||
    "Buyer " + buyerId
  ).trim();

  const mobile = (regDoc?.organizationContact || regDoc?.transactionalMobile || buyerDoc?.mobile || "—").trim();
  const transactionalMobile = (regDoc?.transactionalMobile || "").trim();
  const email = (regDoc?.organizationEmail || regDoc?.transactionalEmail || buyerDoc?.email || "—").trim();
  const transactionalEmail = (regDoc?.transactionalEmail || "").trim();
  const city = (regDoc?.contactCity || regDoc?.shippingLocation?.address?.city || buyerDoc?.city || "—").trim();
  const state = (regDoc?.contactState || regDoc?.shippingLocation?.address?.state || buyerDoc?.state || "—").trim();
  const district = (regDoc?.contactDistrict || regDoc?.shippingLocation?.address?.district || "—").trim();
  const pinCode = (regDoc?.contactPinCode || regDoc?.shippingLocation?.address?.postalCode || "—").trim();
  const streetAddress = (regDoc?.streetAddress || "—").trim();
  const buildingName = (regDoc?.buildingName || "—").trim();
  const shopNumber = (regDoc?.shopNumber || "—").trim();
  const gstin = (regDoc?.gstinNumber || buyerDoc?.gstin || "—").trim();
  const gstrStatus = (regDoc?.gstrStatus || "registered").trim();
  const businessType = (regDoc?.businessType || buyerDoc?.buyerType || buyerDoc?.businessType || "corporate").trim();
  const teamSize = (regDoc?.teamSize || "—").trim();
  const jmiOfficerID = (regDoc?.jmiOfficerID || "Not Assigned").trim();

  // Bank details
  const bankName = (regDoc?.bankName || "—").trim();
  const accountHolderName = (regDoc?.accountHolderName || buyerName).trim();
  const accountNumber = (regDoc?.accountNumber || "—").trim();
  const confirmAccountNumber = (regDoc?.confirmAccountNumber || "—").trim();
  const ifscCode = (regDoc?.ifscCode || "—").trim();
  const accountType = (regDoc?.accountType || "Savings").trim();
  const isAccountMatched = accountNumber && confirmAccountNumber && accountNumber !== "—" && accountNumber === confirmAccountNumber;

  // Security passkey
  const privatePasskey = regDoc?.privatePasskey || [];
  const hasPasskey = Array.isArray(privatePasskey) ? privatePasskey.length > 0 : !!privatePasskey;

  // Shipping Location & Geolocation
  const shippingLocation = regDoc?.shippingLocation || null;
  const addressString = shippingLocation?.addressString || (city !== "—" ? `${shopNumber !== "—" ? shopNumber + ", " : ""}${streetAddress !== "—" ? streetAddress + ", " : ""}${city}, ${state} ${pinCode}` : "—");
  const shippingLatitude = regDoc?.shippingLatitude ?? shippingLocation?.latitude ?? null;
  const shippingLongitude = regDoc?.shippingLongitude ?? shippingLocation?.longitude ?? null;

  const rawStatus = (buyerDoc?.status || "pending").toLowerCase();
  const isSuspended = rawStatus === "suspended";
  const isBlocked = rawStatus === "blocked";
  const isActive = rawStatus === "active" || rawStatus === "verified";

  // Verification status
  const kycStatus =
    buyerDoc?.verificationStatus ||
    (gstin && gstin !== "—" ? "verified" : "pending");

  /* ── Unified Documents Collection ── */
  const documents = useMemo(() => {
    const list = [];

    // 1. Business Documents from registration (support BOTH businessDocumentIds AND businessDocumentUrls)
    const docIds = Array.isArray(regDoc?.businessDocumentIds) ? regDoc.businessDocumentIds : [];
    const docUrls = Array.isArray(regDoc?.businessDocumentUrls) ? regDoc.businessDocumentUrls : [];
    const maxDocs = Math.max(docIds.length, docUrls.length);

    for (let i = 0; i < maxDocs; i++) {
      const docId = docIds[i] || "";
      const storedUrl = docUrls[i] || "";
      const isPdf = isPdfString(docId) || isPdfString(storedUrl);
      const primaryUrl = buildCloudinaryUrl(docId, storedUrl);
      if (!primaryUrl && !docId) continue;

      let title = `Business Registration Document #${i + 1}`;
      let category = "Business Proof / License";

      if (docId.includes("GST") || storedUrl.includes("GST")) {
        title = "GST Registration Certificate (Form REG-06)";
        category = "Government Tax Document";
      } else if (docId.includes("PAN") || storedUrl.includes("PAN")) {
        title = "Permanent Account Number (PAN Card)";
        category = "Identity & Tax Proof";
      } else if (docId.includes("LICENSE") || storedUrl.includes("LICENSE")) {
        title = "Trade / Business License Proof";
        category = "Statutory Business License";
      }

      list.push({
        id: `biz_doc_${i}`,
        title,
        category,
        docId,
        url: primaryUrl,
        rawUrl: primaryUrl,
        renderUrl: isPdf ? getPdfPageRenderUrl(docId, storedUrl) : primaryUrl,
        downloadUrl: buildCloudinaryDownloadUrl(docId, storedUrl),
        thumbUrl: getThumbnailUrl(docId, storedUrl, isPdf),
        isPdf,
        isImg: !isPdf,
        type: isPdf ? "PDF Document" : "Business Document",
        status: buyerDoc?.docStatuses?.[`biz_doc_${i}`] || "verified",
        uploadedAt: regDoc?.createdAt || buyerDoc?.createdAt,
      });
    }

    // 2. Organization / Premises Photos
    const orgPhotoIds = Array.isArray(regDoc?.organizationPhotoIds) ? regDoc.organizationPhotoIds : [];
    const orgPhotoUrls = Array.isArray(regDoc?.organizationPhotoUrls) ? regDoc.organizationPhotoUrls : [];
    const maxOrgPhotos = Math.max(orgPhotoIds.length, orgPhotoUrls.length);

    for (let i = 0; i < maxOrgPhotos; i++) {
      const pId = orgPhotoIds[i] || "";
      const pStoredUrl = orgPhotoUrls[i] || "";
      const pIsPdf = isPdfString(pId) || isPdfString(pStoredUrl);
      const pUrl = buildCloudinaryUrl(pId, pStoredUrl);
      if (!pUrl) continue;

      list.push({
        id: `org_photo_${i}`,
        title: `Store Premises / Front Photo #${i + 1}`,
        category: "Store Photo",
        docId: pId,
        url: pUrl,
        rawUrl: pUrl,
        renderUrl: pIsPdf ? getPdfPageRenderUrl(pId, pStoredUrl) : pUrl,
        downloadUrl: buildCloudinaryDownloadUrl(pId, pStoredUrl),
        thumbUrl: getThumbnailUrl(pId, pStoredUrl, pIsPdf),
        isPdf: pIsPdf,
        isImg: !pIsPdf,
        type: pIsPdf ? "PDF Document" : "Premises Photo",
        status: buyerDoc?.docStatuses?.[`org_photo_${i}`] || "verified",
        uploadedAt: regDoc?.createdAt || buyerDoc?.createdAt,
      });
    }

    // 3. Store Brand Logo
    const storeLogoId = regDoc?.storeLogoId;
    const storeLogoUrl = buildCloudinaryUrl(storeLogoId, regDoc?.storeLogoUrl);
    if (storeLogoUrl) {
      list.push({
        id: "store_logo",
        title: "Store Brand Logo",
        category: "Brand Asset",
        docId: storeLogoId || "",
        url: storeLogoUrl,
        rawUrl: storeLogoUrl,
        renderUrl: storeLogoUrl,
        downloadUrl: buildCloudinaryDownloadUrl(storeLogoId, regDoc?.storeLogoUrl),
        thumbUrl: storeLogoUrl,
        isPdf: false,
        isImg: true,
        type: "Logo",
        status: "verified",
        uploadedAt: regDoc?.createdAt || buyerDoc?.createdAt,
      });
    }

    // 4. Dealing Person Photos
    if (regDoc?.dealingPersons && Array.isArray(regDoc.dealingPersons)) {
      regDoc.dealingPersons.forEach((person, idx) => {
        const photoId = person?.photoId;
        const photoUrl = buildCloudinaryUrl(photoId, person?.photoUrl);
        if (photoUrl) {
          const personIsPdf = isPdfString(photoId) || isPdfString(photoUrl);
          list.push({
            id: `person_photo_${idx}`,
            title: `Contact Photo: ${person.fullName || `Person #${idx + 1}`}`,
            category: "Identity Proof",
            docId: photoId || "",
            url: photoUrl,
            rawUrl: photoUrl,
            renderUrl: personIsPdf ? getPdfPageRenderUrl(photoId, photoUrl) : photoUrl,
            downloadUrl: buildCloudinaryDownloadUrl(photoId, photoUrl),
            thumbUrl: getThumbnailUrl(photoId, photoUrl, personIsPdf),
            isPdf: personIsPdf,
            isImg: !personIsPdf,
            type: personIsPdf ? "PDF Document" : "Photo ID",
            status: "verified",
            uploadedAt: regDoc?.createdAt,
          });
        }
      });
    }

    // 5. Explicit Document Fields
    const addDoc = (id, title, category, urlOrId, statusKey) => {
      if (!urlOrId) return;
      const isPdf = isPdfString(urlOrId);
      const primaryUrl = buildCloudinaryUrl(urlOrId, typeof urlOrId === "string" && urlOrId.startsWith("http") ? urlOrId : "");
      list.push({
        id,
        title,
        category,
        docId: "",
        url: primaryUrl,
        rawUrl: primaryUrl,
        renderUrl: isPdf ? getPdfPageRenderUrl("", primaryUrl) : primaryUrl,
        downloadUrl: buildCloudinaryDownloadUrl("", primaryUrl),
        thumbUrl: getThumbnailUrl("", primaryUrl, isPdf),
        isPdf,
        isImg: !isPdf,
        type: isPdf ? "PDF Document" : "Document",
        status: buyerDoc?.docStatuses?.[statusKey] || "verified",
        uploadedAt: regDoc?.createdAt,
      });
    };

    addDoc("gst_certificate", "GST Registration Certificate", "Tax Document",
      regDoc?.gstCertificateUrl || buyerDoc?.gstCertificateUrl, "gst_certificate");
    addDoc("pan_card", "PAN Card Document", "Tax Document",
      regDoc?.panCardUrl || buyerDoc?.panCardUrl, "pan_card");
    addDoc("cancelled_cheque", "Bank Cancelled Cheque / Passbook", "Banking Document",
      regDoc?.cancelledChequeUrl || buyerDoc?.cancelledChequeUrl, "cancelled_cheque");

    // 6. Generic/extra documents array
    if (regDoc?.documents && Array.isArray(regDoc.documents)) {
      regDoc.documents.forEach((d, i) => {
        if (d?.url) {
          const isPdf = isPdfString(d.url);
          const primaryUrl = buildCloudinaryUrl("", d.url);
          list.push({
            id: `doc_extra_${i}`,
            title: d.name || `Document #${i + 1}`,
            category: d.type || "Official Document",
            docId: "",
            url: primaryUrl,
            rawUrl: primaryUrl,
            renderUrl: isPdf ? getPdfPageRenderUrl("", primaryUrl) : primaryUrl,
            downloadUrl: buildCloudinaryDownloadUrl("", primaryUrl),
            thumbUrl: getThumbnailUrl("", primaryUrl, isPdf),
            isPdf,
            isImg: !isPdf,
            type: isPdf ? "PDF Document" : "Document",
            status: d.status || "verified",
            uploadedAt: d.uploadedAt || regDoc.createdAt,
          });
        }
      });
    }

    return list;
  }, [regDoc, buyerDoc]);


  /* ── Merged Orders & History ── */
  const allOrdersList = useMemo(() => {
    const list = [
      ...orders.map((o) => ({
        ...o,
        orderId: o.orderId || o.id,
        isSettled: false,
        displayStatus: o.status || "Accepted",
        date: o.createdAt || o.timestamp,
        amount: safeNum(o.totalAmount || o.calculatedPrice || o.price),
        fineWt: safeNum(o.fineWeight || o.totalFineWt),
        grossWt: safeNum(o.grossWeight || o.totalGrossWt),
      })),
      ...settledOrders.map((s) => ({
        ...s,
        orderId: s.settlementId || s.id,
        isSettled: true,
        displayStatus: "Settled / Delivered",
        date: s.settlementTimestamp || s.createdAt,
        amount: safeNum(
          s.paymentDetails?.totalAmountPaid || s.groupSummary?.totalAmount
        ),
        fineWt: safeNum(
          s.paymentDetails?.totalFineWtDelivered ||
            s.groupSummary?.displayFineWeight
        ),
        grossWt: safeNum(s.groupSummary?.totalGrossWeight),
        itemCount:
          s.paymentDetails?.paidQtySum ?? s.groupSummary?.totalItems ?? 1,
      })),
    ];

    return list.sort((a, b) => {
      const ta = a.date?.toDate?.() || new Date(a.date || 0);
      const tb = b.date?.toDate?.() || new Date(b.date || 0);
      return tb - ta;
    });
  }, [orders, settledOrders]);

  /* ── Filtered Orders ── */
  const filteredOrders = useMemo(() => {
    return allOrdersList.filter((o) => {
      const q = orderSearch.toLowerCase();
      const matchSearch =
        !q ||
        o.orderId.toLowerCase().includes(q) ||
        (o.displayStatus || "").toLowerCase().includes(q) ||
        (o.sellerId || "").toLowerCase().includes(q);

      if (!matchSearch) return false;

      if (orderSubTab === "in_progress") {
        return !o.isSettled && o.displayStatus !== "Cancelled";
      }
      if (orderSubTab === "settled") {
        return (
          o.isSettled ||
          o.displayStatus === "Settled / Delivered" ||
          o.displayStatus === "Delivered"
        );
      }
      if (orderSubTab === "cancelled") {
        return o.displayStatus === "Cancelled";
      }
      return true;
    });
  }, [allOrdersList, orderSubTab, orderSearch]);

  /* ── Key Metrics ── */
  const totalOrdersCount = allOrdersList.length;
  const inProgressCount = allOrdersList.filter((o) => !o.isSettled).length;
  const totalSettledAmount = settledOrders.reduce((acc, s) => {
    return (
      acc +
      safeNum(
        s.paymentDetails?.totalAmountPaid || s.groupSummary?.totalAmount || 0
      )
    );
  }, 0);

  /* ── Actions: Update Status ── */
  const handleUpdateStatus = async () => {
    try {
      setStatusUpdating(true);
      await updateDoc(doc(db, "buyers", buyerId), {
        status: selectedStatus,
        statusUpdatedReason: statusReason,
        statusUpdatedAt: serverTimestamp(),
      });
      setShowStatusModal(false);
      setStatusReason("");
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status: " + err.message);
    } finally {
      setStatusUpdating(false);
    }
  };

  /* ── Actions: Update Document Status ── */
  const handleVerifyDocument = async (docId, newStatus) => {
    try {
      const docStatuses = buyerDoc?.docStatuses || {};
      docStatuses[docId] = newStatus;
      await updateDoc(doc(db, "buyers", buyerId), {
        docStatuses,
        lastDocVerificationAt: serverTimestamp(),
      });
      if (previewDoc && previewDoc.id === docId) {
        setPreviewDoc({ ...previewDoc, status: newStatus });
      }
    } catch (err) {
      console.error("Error updating doc status:", err);
      alert("Failed to update document status: " + err.message);
    }
  };

  /* ── Loading State ── */
  if (loading) {
    return (
      <Layout title="Buyer 360° Profile">
        <div className="b360-loading-state">
          <div className="b360-spinner" />
          <p>Loading real-time profile for Buyer {buyerId}...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Buyer 360° — ${buyerName}`}>
      <div className="b360-page">
        {/* Breadcrumb */}
        <div className="b360-breadcrumb">
          <button
            className="b360-breadcrumb-btn"
            onClick={() => navigate("/buyers")}
          >
            <ArrowLeft size={14} /> Back to All Buyers
          </button>
          <span>/</span>
          <span>Buyer 360°</span>
          <span>/</span>
          <strong style={{ color: "#1e293b" }}>{buyerId}</strong>
        </div>

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: "10px 16px", borderRadius: "8px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* ── Persistent Header ────────────────────────────── */}
        <div className="b360-header-card">
          <div className="b360-header-top">
            {/* Identity */}
            <div className="b360-header-identity">
              {regDoc?.storeLogoUrl ? (
                <img
                  src={regDoc.storeLogoUrl}
                  alt={buyerName}
                  className="b360-avatar"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div
                  className="b360-avatar"
                  style={{ background: getGradient(buyerName) }}
                >
                  {getInitials(buyerName)}
                </div>
              )}

              <div className="b360-identity-info">
                <h1 className="b360-buyer-name">{buyerName}</h1>
                <div className="b360-buyer-id">{buyerId}</div>
                <div className="b360-header-meta">
                  <span className="b360-meta-item">
                    <Phone size={13} style={{ color: "#8a6b32" }} />
                    <strong>{mobile}</strong>
                  </span>
                  <span>•</span>
                  <span className="b360-meta-item">
                    <MapPin size={13} style={{ color: "#8a6b32" }} />
                    {city}, {state}
                  </span>
                  <span>•</span>
                  <span className="b360-meta-item">
                    <Building2 size={13} style={{ color: "#8a6b32" }} />
                    GSTIN: <strong>{gstin}</strong>
                  </span>
                  <span>•</span>
                  <span className="b360-meta-item">
                    <Users size={13} style={{ color: "#8a6b32" }} />
                    {businessType}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="b360-header-actions">
              <button
                className="b360-btn-edit"
                onClick={() => {
                  setSelectedStatus(rawStatus);
                  setShowStatusModal(true);
                }}
              >
                <Edit3 size={14} /> Change Status
              </button>

              <div className="b360-more-wrapper">
                <button
                  className="b360-btn-more"
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                >
                  Actions <ChevronDown size={14} />
                </button>
                {showMoreMenu && (
                  <div className="b360-dropdown">
                    <button
                      className="b360-dropdown-item"
                      onClick={() => {
                        setActiveTab("kyc");
                        setShowMoreMenu(false);
                      }}
                    >
                      <FileCheck size={14} /> Review KYC Documents ({documents.length})
                    </button>
                    <button
                      className="b360-dropdown-item"
                      onClick={() => {
                        setActiveTab("orders");
                        setShowMoreMenu(false);
                      }}
                    >
                      <ShoppingBag size={14} /> View All Orders ({allOrdersList.length})
                    </button>
                    <button
                      className="b360-dropdown-item"
                      onClick={() => {
                        window.open(`tel:${mobile}`, "_self");
                        setShowMoreMenu(false);
                      }}
                    >
                      <Phone size={14} /> Call Buyer (+91 {mobile})
                    </button>
                    <div className="b360-dropdown-divider" />
                    <button
                      className="b360-dropdown-item danger"
                      onClick={() => {
                        setSelectedStatus(isBlocked ? "active" : "blocked");
                        setShowStatusModal(true);
                        setShowMoreMenu(false);
                      }}
                    >
                      <Ban size={14} /> {isBlocked ? "Unblock Buyer" : "Block Buyer"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Row */}
          <div className="b360-status-row">
            <span
              className={`b360-status-chip ${
                isActive
                  ? "chip-active"
                  : isSuspended
                  ? "chip-suspended"
                  : isBlocked
                  ? "chip-blocked"
                  : "chip-pending"
              }`}
            >
              {isActive && <CheckCircle size={12} />}
              {isSuspended && <AlertCircle size={12} />}
              {isBlocked && <Ban size={12} />}
              ACCOUNT: {(rawStatus || "PENDING").toUpperCase()}
            </span>

            <span
              className={`b360-status-chip ${
                kycStatus === "verified"
                  ? "chip-verified"
                  : kycStatus === "kyc_review"
                  ? "chip-kyc_review"
                  : "chip-pending"
              }`}
            >
              <ShieldCheck size={12} />
              KYC: {(kycStatus || "PENDING").toUpperCase()}
            </span>

            <span className="b360-status-chip chip-credit-off">
              DOCUMENTS: {documents.length} ATTACHED
            </span>
          </div>
        </div>

        {/* ── Tab Navigation Bar (Focused 5 Modules) ───────── */}
        <div className="b360-tabs-bar">
          <button
            className={`b360-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <Activity size={15} /> Overview
          </button>
          <button
            className={`b360-tab ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            <Building2 size={15} /> Business Profile
          </button>
          <button
            className={`b360-tab ${activeTab === "kyc" ? "active" : ""}`}
            onClick={() => setActiveTab("kyc")}
          >
            <FileText size={15} /> KYC & Documents ({documents.length})
          </button>
          <button
            className={`b360-tab ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <Users size={15} /> Contacts & Users ({regDoc?.dealingPersons?.length || 1})
          </button>
          <button
            className={`b360-tab ${activeTab === "orders" ? "active" : ""}`}
            onClick={() => setActiveTab("orders")}
          >
            <ShoppingBag size={15} /> Orders & History ({allOrdersList.length})
          </button>
        </div>

        {/* ── TAB CONTENT ─────────────────────────────────── */}
        <div className="b360-tab-content">
          {/* ══════════════════════════════════════════════════
              TAB 1: OVERVIEW
             ══════════════════════════════════════════════════ */}
          {activeTab === "overview" && (
            <div>
              {/* Summary KPIs */}
              <div className="b360-summary-grid">
                <div className="b360-summary-card">
                  <span className="b360-summary-label">Total Orders</span>
                  <span className="b360-summary-value">{totalOrdersCount}</span>
                  <span className="b360-summary-sub">
                    {inProgressCount} in progress • {settledOrders.length} settled
                  </span>
                </div>

                <div className="b360-summary-card">
                  <span className="b360-summary-label">Settled GMV</span>
                  <span className="b360-summary-value" style={{ color: "#10b981" }}>
                    {fmtShort(totalSettledAmount)}
                  </span>
                  <span className="b360-summary-sub">
                    {settledOrders.length} completed settlements
                  </span>
                </div>

                <div className="b360-summary-card">
                  <span className="b360-summary-label">KYC Documents</span>
                  <span className="b360-summary-value">{documents.length}</span>
                  <span className="b360-summary-sub">Proofs & photos verified</span>
                </div>

                <div className="b360-summary-card">
                  <span className="b360-summary-label">Orders In Progress</span>
                  <span className="b360-summary-value" style={{ color: inProgressCount > 0 ? "#8a6b32" : "#1e293b" }}>
                    {inProgressCount}
                  </span>
                  <span className="b360-summary-sub">Active live orders</span>
                </div>
              </div>

              {/* Health and Highlights */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div className="b360-card">
                  <h3 className="b360-card-title">
                    <ShieldCheck size={16} /> Buyer Health & Compliance
                  </h3>
                  <div className="b360-health-grid">
                    <div className="b360-health-item">
                      <span className="b360-health-label">Account Standing</span>
                      <span className={isActive ? "b360-health-ok" : "b360-health-warn"}>
                        {rawStatus.toUpperCase()}
                      </span>
                    </div>
                    <div className="b360-health-item">
                      <span className="b360-health-label">KYC Verification</span>
                      <span className={kycStatus === "verified" ? "b360-health-ok" : "b360-health-warn"}>
                        {kycStatus.toUpperCase()}
                      </span>
                    </div>
                    <div className="b360-health-item">
                      <span className="b360-health-label">Documents Attached</span>
                      <span className={documents.length > 0 ? "b360-health-ok" : "b360-health-bad"}>
                        {documents.length} verified
                      </span>
                    </div>
                    <div className="b360-health-item">
                      <span className="b360-health-label">GSTR Status</span>
                      <span className="b360-health-ok">
                        {regDoc?.gstrStatus || "REGISTERED"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="b360-card">
                  <h3 className="b360-card-title">
                    <Activity size={16} /> Key Account Identifiers
                  </h3>
                  <div className="b360-field-grid">
                    <div className="b360-field">
                      <span className="b360-field-label">Buyer ID</span>
                      <span className="b360-field-value" style={{ fontFamily: "monospace", color: "#8a6b32" }}>
                        {buyerId}
                      </span>
                    </div>
                    <div className="b360-field">
                      <span className="b360-field-label">Registered Phone</span>
                      <span className="b360-field-value">{mobile}</span>
                    </div>
                    <div className="b360-field">
                      <span className="b360-field-label">GSTIN / Tax ID</span>
                      <span className="b360-field-value">{gstin}</span>
                    </div>
                    <div className="b360-field">
                      <span className="b360-field-label">Registered On</span>
                      <span className="b360-field-value">
                        {formatDate(buyerDoc?.createdAt || regDoc?.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Document Strip */}
              <div className="b360-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h3 className="b360-card-title" style={{ margin: 0, border: "none" }}>
                    <FileText size={16} /> Attached Documents & Proofs ({documents.length})
                  </h3>
                  <button
                    className="b360-doc-btn primary"
                    onClick={() => setActiveTab("kyc")}
                  >
                    View All in Documents Tab →
                  </button>
                </div>

                {documents.length === 0 ? (
                  <div className="b360-empty-state">
                    No business or KYC documents uploaded yet.
                  </div>
                ) : (
                  <div className="b360-doc-grid">
                    {documents.slice(0, 4).map((d) => (
                      <div key={d.id} className="b360-doc-card">
                        <div
                          className="b360-doc-preview-box"
                          onClick={() => setPreviewDoc(d)}
                        >
                          {d.isPdf ? (
                            <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span className="b360-pdf-badge">PDF</span>
                              {d.thumbUrl ? (
                                <img
                                  src={d.thumbUrl}
                                  alt={d.title}
                                  className="b360-doc-img-thumb"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const fb = e.currentTarget.parentElement?.querySelector(".b360-pdf-placeholder");
                                    if (fb) fb.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className="b360-pdf-placeholder"
                                style={{ display: d.thumbUrl ? "none" : "flex" }}
                              >
                                <FileText size={34} style={{ color: "#dc2626" }} />
                                <span className="b360-pdf-placeholder-title">{d.title}</span>
                                <span className="b360-pdf-placeholder-hint">Click to Preview PDF</span>
                              </div>
                            </div>
                          ) : d.isImg ? (
                            <img src={d.url} alt={d.title} className="b360-doc-img-thumb" />
                          ) : (
                            <div className="b360-doc-icon-preview">
                              <FileText size={32} />
                              <span>{d.type}</span>
                            </div>
                          )}
                          <button className="b360-doc-overlay-btn">
                            <Eye size={12} /> {d.isPdf ? "View PDF" : "Preview"}
                          </button>
                        </div>
                        <div className="b360-doc-info">
                          <h4 className="b360-doc-title">
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {d.title}
                            </span>
                            <span className={`b360-doc-badge ${d.status}`}>
                              {d.status}
                            </span>
                          </h4>
                          <p className="b360-doc-subtext">{d.category}</p>
                          <div className="b360-doc-actions">
                            <button
                              className="b360-doc-btn"
                              onClick={() => setPreviewDoc(d)}
                            >
                              <Eye size={12} /> Full View
                            </button>
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              className="b360-doc-btn"
                            >
                              <ExternalLink size={12} /> Open
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Orders Preview */}
              <div className="b360-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <h3 className="b360-card-title" style={{ margin: 0, border: "none" }}>
                    <ShoppingBag size={16} /> Recent Orders & Settlement History
                  </h3>
                  <button
                    className="b360-doc-btn primary"
                    onClick={() => setActiveTab("orders")}
                  >
                    View All Orders ({allOrdersList.length}) →
                  </button>
                </div>

                {allOrdersList.length === 0 ? (
                  <div className="b360-empty-state">No orders recorded for this buyer yet.</div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="b360-data-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Gross Wt</th>
                          <th>Fine Wt</th>
                          <th>Total Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allOrdersList.slice(0, 5).map((o) => (
                          <tr key={o.id}>
                            <td style={{ fontFamily: "monospace", fontWeight: 700, color: "#8a6b32" }}>
                              {o.orderId}
                            </td>
                            <td>{formatDate(o.date)}</td>
                            <td>
                              <span
                                className={`b360-status-chip ${
                                  o.isSettled ? "chip-verified" : "chip-active"
                                }`}
                              >
                                {o.displayStatus}
                              </span>
                            </td>
                            <td>{o.grossWt > 0 ? `${o.grossWt.toFixed(3)}g` : "—"}</td>
                            <td>{o.fineWt > 0 ? `${o.fineWt.toFixed(3)}g` : "—"}</td>
                            <td style={{ fontWeight: 700 }}>{fmt(o.amount)}</td>
                            <td>
                              <button
                                className="b360-doc-action-btn"
                                onClick={() => {
                                  setActiveTab("orders");
                                  setExpandedOrder(o.id);
                                }}
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB 2: BUSINESS PROFILE
             ══════════════════════════════════════════════════ */}
          {activeTab === "profile" && (
            <div>
              {/* Organization Profile */}
              <div className="b360-card">
                <h3 className="b360-card-title">
                  <Building2 size={16} /> Organization Profile & Registration
                </h3>
                <div className="b360-field-grid">
                  <div className="b360-field">
                    <span className="b360-field-label">Legal Organization Name</span>
                    <span className="b360-field-value">{regDoc?.organizationName || buyerName}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Business Type</span>
                    <span className="b360-field-value">{businessType}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">GSTIN / UIN</span>
                    <span className="b360-field-value">{gstin}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">GSTR Status</span>
                    <span className="b360-field-value" style={{ textTransform: "capitalize", fontWeight: 600 }}>
                      <span className="b360-badge-match" style={{ marginLeft: 0 }}>
                        <CheckCircle size={11} /> {gstrStatus}
                      </span>
                    </span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Primary Organization Contact</span>
                    <span className="b360-field-value" style={{ fontFamily: "monospace" }}>{mobile}</span>
                  </div>
                  {transactionalMobile && transactionalMobile !== mobile && (
                    <div className="b360-field">
                      <span className="b360-field-label">Transactional Mobile</span>
                      <span className="b360-field-value" style={{ fontFamily: "monospace" }}>{transactionalMobile}</span>
                    </div>
                  )}
                  <div className="b360-field">
                    <span className="b360-field-label">Organization Email</span>
                    <span className="b360-field-value">{email}</span>
                  </div>
                  {transactionalEmail && transactionalEmail !== email && (
                    <div className="b360-field">
                      <span className="b360-field-label">Transactional Email</span>
                      <span className="b360-field-value">{transactionalEmail}</span>
                    </div>
                  )}
                  <div className="b360-field">
                    <span className="b360-field-label">Team / Employee Size</span>
                    <span className="b360-field-value">{teamSize}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">JMI Relationship Officer</span>
                    <span className="b360-field-value" style={{ fontFamily: "monospace" }}>{jmiOfficerID}</span>
                  </div>
                </div>
              </div>

              {/* Physical Address & Coordinates */}
              <div className="b360-card">
                <h3 className="b360-card-title">
                  <MapPin size={16} /> Business Location & Shipping Address
                </h3>
                <div className="b360-field-grid">
                  <div className="b360-field">
                    <span className="b360-field-label">Shop / Office Number</span>
                    <span className="b360-field-value">{shopNumber}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Building / Landmark</span>
                    <span className="b360-field-value">{buildingName}</span>
                  </div>
                  <div className="b360-field" style={{ gridColumn: "1 / -1" }}>
                    <span className="b360-field-label">Street Address</span>
                    <span className="b360-field-value">{streetAddress}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">City</span>
                    <span className="b360-field-value">{city}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">District</span>
                    <span className="b360-field-value">{district}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">State</span>
                    <span className="b360-field-value">{state}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">PIN Code</span>
                    <span className="b360-field-value">{pinCode}</span>
                  </div>
                  <div className="b360-field" style={{ gridColumn: "1 / -1" }}>
                    <span className="b360-field-label">Full Address</span>
                    <span className="b360-field-value" style={{ fontWeight: 600, color: "#1e293b" }}>{addressString}</span>
                  </div>

                  {/* Geolocation */}
                  {(shippingLatitude || shippingLongitude) && (
                    <div className="b360-field" style={{ gridColumn: "1 / -1" }}>
                      <span className="b360-field-label">GPS Coordinates</span>
                      <span className="b360-field-value">
                        {shippingLatitude}, {shippingLongitude}{" "}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${shippingLatitude},${shippingLongitude}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ marginLeft: "8px", color: "#8a6b32", fontSize: "12px", fontWeight: 700 }}
                        >
                          View on Google Maps →
                        </a>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bank Account Details */}
              <div className="b360-card">
                <h3 className="b360-card-title">
                  <Building2 size={16} /> Settlement & Bank Account
                </h3>
                <div className="b360-field-grid">
                  <div className="b360-field">
                    <span className="b360-field-label">Bank Name</span>
                    <span className="b360-field-value" style={{ fontWeight: 700 }}>{bankName}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Account Holder Name</span>
                    <span className="b360-field-value">{accountHolderName}</span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Account Number</span>
                    <span className="b360-field-value" style={{ fontFamily: "monospace", letterSpacing: "1px", fontWeight: 700 }}>
                      {accountNumber}
                    </span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Confirmed Account Number</span>
                    <span className="b360-field-value" style={{ fontFamily: "monospace", letterSpacing: "1px" }}>
                      {confirmAccountNumber}
                      {isAccountMatched && (
                        <span className="b360-badge-match">
                          <CheckCircle size={11} /> Matched
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">IFSC Code</span>
                    <span className="b360-field-value" style={{ fontFamily: "monospace", fontWeight: 700, color: "#8a6b32" }}>
                      {ifscCode}
                    </span>
                  </div>
                  <div className="b360-field">
                    <span className="b360-field-label">Account Type</span>
                    <span className="b360-field-value" style={{ textTransform: "capitalize" }}>
                      {accountType}
                    </span>
                  </div>
                </div>
              </div>

              {/* Security & Authentication Passkey */}
              {hasPasskey && (
                <div className="b360-card">
                  <h3 className="b360-card-title">
                    <ShieldCheck size={16} /> Security & Passkey Authentication
                  </h3>
                  <div className="b360-field-grid">
                    <div className="b360-field" style={{ gridColumn: "1 / -1" }}>
                      <span className="b360-field-label">Private 6-Digit Buyer Authentication Passkey</span>
                      <div className="b360-passkey-container">
                        <div className="b360-passkey-digits">
                          {(Array.isArray(privatePasskey) ? privatePasskey : String(privatePasskey).split("")).map((digit, idx) => (
                            <div key={idx} className="b360-passkey-digit">
                              {showPasskey ? digit : "•"}
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="b360-passkey-toggle-btn"
                          onClick={() => setShowPasskey(!showPasskey)}
                        >
                          {showPasskey ? <EyeOff size={13} /> : <Eye size={13} />}
                          {showPasskey ? "Hide Passkey" : "Reveal Passkey"}
                        </button>
                        <span className="b360-badge-match">
                          <CheckCircle size={11} /> Synchronized & Configured
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB 3: KYC & DOCUMENTS (Full Visibility)
             ══════════════════════════════════════════════════ */}
          {activeTab === "kyc" && (
            <div>
              {/* Overall status banner */}
              <div className={`b360-kyc-overall ${kycStatus === "verified" ? "verified" : "pending"}`}>
                <ShieldCheck size={20} />
                <div>
                  Overall Verification Status: <strong>{kycStatus.toUpperCase()}</strong>
                  <div style={{ fontSize: "12px", fontWeight: 400, marginTop: "2px" }}>
                    {documents.length} verified documents & photographs on record
                  </div>
                </div>
              </div>

              {/* Grid of All Documents */}
              <div className="b360-card">
                <h3 className="b360-card-title">
                  <FileCheck size={16} /> All Uploaded Business Documents & Identity Proofs ({documents.length})
                </h3>

                {documents.length === 0 ? (
                  <div className="b360-empty-state">
                    <FileText size={40} style={{ color: "#cbd5e1", marginBottom: "10px" }} />
                    <p>No documents uploaded yet for this buyer profile.</p>
                  </div>
                ) : (
                  <div className="b360-doc-grid">
                    {documents.map((d) => (
                      <div key={d.id} className="b360-doc-card">
                        <div
                          className="b360-doc-preview-box"
                          onClick={() => setPreviewDoc(d)}
                        >
                          {d.isPdf ? (
                            <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span className="b360-pdf-badge">PDF</span>
                              {d.thumbUrl ? (
                                <img
                                  src={d.thumbUrl}
                                  alt={d.title}
                                  className="b360-doc-img-thumb"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const fb = e.currentTarget.parentElement?.querySelector(".b360-pdf-placeholder");
                                    if (fb) fb.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className="b360-pdf-placeholder"
                                style={{ display: d.thumbUrl ? "none" : "flex" }}
                              >
                                <FileText size={38} style={{ color: "#dc2626" }} />
                                <span className="b360-pdf-placeholder-title">{d.title}</span>
                                <span className="b360-pdf-placeholder-hint">Click to Preview PDF</span>
                              </div>
                            </div>
                          ) : d.isImg ? (
                            <img src={d.url} alt={d.title} className="b360-doc-img-thumb" />
                          ) : (
                            <div className="b360-doc-icon-preview">
                              <FileText size={36} />
                              <span>{d.type}</span>
                            </div>
                          )}
                          <button className="b360-doc-overlay-btn">
                            <Eye size={12} /> {d.isPdf ? "View PDF" : "View Modal"}
                          </button>
                        </div>
                        <div className="b360-doc-info">
                          <h4 className="b360-doc-title">
                            <span title={d.title} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {d.title}
                            </span>
                            <span className={`b360-doc-badge ${d.status}`}>
                              {d.status}
                            </span>
                          </h4>
                          <p className="b360-doc-subtext">{d.category}</p>

                          <div className="b360-doc-actions">
                            <button
                              className="b360-doc-btn primary"
                              onClick={() => setPreviewDoc(d)}
                            >
                              <Eye size={12} /> {d.isPdf ? "Inspect PDF" : "Inspect"}
                            </button>
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              className="b360-doc-btn"
                            >
                              <ExternalLink size={12} /> Open Link
                            </a>
                            <a
                              href={d.url}
                              download
                              className="b360-doc-btn"
                              title="Download File"
                            >
                              <Download size={12} />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Table Breakdown of Documents */}
              <div className="b360-card">
                <h3 className="b360-card-title">
                  <FileText size={16} /> Document Compliance Audit Table
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table className="b360-doc-table">
                    <thead>
                      <tr>
                        <th>Document / Asset</th>
                        <th>Category</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Uploaded On</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((d) => (
                        <tr key={d.id}>
                          <td>
                            <strong>{d.title}</strong>
                          </td>
                          <td>{d.category}</td>
                          <td>{d.type}</td>
                          <td>
                            <span className={`b360-doc-badge ${d.status}`}>
                              {d.status}
                            </span>
                          </td>
                          <td>{formatDate(d.uploadedAt)}</td>
                          <td>
                            <button
                              className="b360-doc-action-btn"
                              onClick={() => setPreviewDoc(d)}
                            >
                              Inspect
                            </button>
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              className="b360-doc-action-btn"
                            >
                              External
                            </a>
                            <button
                              className="b360-doc-action-btn"
                              style={{ color: "#166534" }}
                              onClick={() => handleVerifyDocument(d.id, "verified")}
                            >
                              Verify
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB 4: USERS & CONTACTS
             ══════════════════════════════════════════════════ */}
          {activeTab === "users" && (
            <div>
              <div className="b360-card">
                <h3 className="b360-card-title">
                  <Users size={16} /> Registered Dealing Persons & Contact Team
                </h3>

                {(!regDoc?.dealingPersons || regDoc.dealingPersons.length === 0) ? (
                  <div className="b360-empty-state">
                    No authorized dealing persons added during registration.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="b360-users-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Designation / Role</th>
                          <th>Department</th>
                          <th>Contact Number</th>
                          <th>Email Address</th>
                          <th>Photo ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {regDoc.dealingPersons.map((p, idx) => (
                          <tr key={idx}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                {p.photoUrl ? (
                                  <img
                                    src={p.photoUrl}
                                    alt={p.fullName}
                                    className="b360-user-avatar"
                                    style={{ objectFit: "cover" }}
                                  />
                                ) : (
                                  <div className="b360-user-avatar">
                                    {getInitials(p.fullName)}
                                  </div>
                                )}
                                <div>
                                  <strong>{p.fullName || "Unnamed"}</strong>
                                  {idx === 0 && (
                                    <span style={{ display: "block", fontSize: "10px", color: "#8a6b32", fontWeight: 700 }}>
                                      PRIMARY POC
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="b360-role-badge">{p.role || "Executive"}</span>
                            </td>
                            <td>{p.department || "Management"}</td>
                            <td style={{ fontFamily: "monospace" }}>{p.contactNumber || "—"}</td>
                            <td>{p.email || "—"}</td>
                            <td>
                              {p.photoUrl ? (
                                <button
                                  className="b360-doc-action-btn"
                                  onClick={() =>
                                    setPreviewDoc({
                                      id: `poc_${idx}`,
                                      title: `Photo ID: ${p.fullName}`,
                                      category: "Identity Proof",
                                      url: p.photoUrl,
                                      isImg: true,
                                      type: "ID Photo",
                                      status: "verified",
                                    })
                                  }
                                >
                                  View Photo
                                </button>
                              ) : (
                                <span style={{ color: "#94a3b8", fontSize: "11px" }}>No photo</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB 5: ORDERS & SETTLEMENT HISTORY
             ══════════════════════════════════════════════════ */}
          {activeTab === "orders" && (
            <div>
              {/* Filter bar */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
                <div className="b360-sub-tabs" style={{ margin: 0 }}>
                  <button
                    className={`b360-sub-tab ${orderSubTab === "all" ? "active" : ""}`}
                    onClick={() => setOrderSubTab("all")}
                  >
                    All Orders ({allOrdersList.length})
                  </button>
                  <button
                    className={`b360-sub-tab ${orderSubTab === "in_progress" ? "active" : ""}`}
                    onClick={() => setOrderSubTab("in_progress")}
                  >
                    In Progress ({inProgressCount})
                  </button>
                  <button
                    className={`b360-sub-tab ${orderSubTab === "settled" ? "active" : ""}`}
                    onClick={() => setOrderSubTab("settled")}
                  >
                    Settled / History ({settledOrders.length})
                  </button>
                </div>

                <div style={{ position: "relative", width: "240px" }}>
                  <Search size={14} style={{ position: "absolute", left: "10px", top: "11px", color: "#94a3b8" }} />
                  <input
                    type="text"
                    placeholder="Search order ID or status..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    className="b360-form-input"
                    style={{ paddingLeft: "32px", width: "100%" }}
                  />
                </div>
              </div>

              {/* Order List Table */}
              <div className="b360-card">
                {ordersLoading ? (
                  <div className="b360-loading-state">
                    <div className="b360-spinner" />
                    <p>Loading orders from database...</p>
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="b360-empty-state">
                    No orders match the selected filters.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="b360-data-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Placed / Settled Date</th>
                          <th>Type / Source</th>
                          <th>Gross Wt</th>
                          <th>Fine Wt</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((o) => {
                          const isExpanded = expandedOrder === o.id;
                          const detailEntries = Object.values(o.orderDetail || {});

                          return (
                            <React.Fragment key={o.id}>
                              <tr>
                                <td style={{ fontFamily: "monospace", fontWeight: 700, color: "#8a6b32" }}>
                                  {o.orderId}
                                </td>
                                <td>{formatDate(o.date)}</td>
                                <td>
                                  <span style={{ fontSize: "11px", color: "#64748b" }}>
                                    {o.source === "myorders" ? "Settled Batch" : "Live Order"}
                                  </span>
                                </td>
                                <td>{o.grossWt > 0 ? `${o.grossWt.toFixed(3)}g` : "—"}</td>
                                <td>{o.fineWt > 0 ? `${o.fineWt.toFixed(3)}g` : "—"}</td>
                                <td style={{ fontWeight: 700, color: "#1e293b" }}>
                                  {fmt(o.amount)}
                                </td>
                                <td>
                                  <span
                                    className={`b360-status-chip ${
                                      o.isSettled ? "chip-verified" : "chip-active"
                                    }`}
                                  >
                                    {o.displayStatus}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="b360-doc-action-btn"
                                    onClick={() =>
                                      setExpandedOrder(isExpanded ? null : o.id)
                                    }
                                  >
                                    {isExpanded ? "Hide" : "Inspect"}
                                  </button>
                                </td>
                              </tr>

                              {/* Expanded Row Breakdown */}
                              {isExpanded && (
                                <tr>
                                  <td colSpan={8} style={{ padding: 0, background: "#f8fafc" }}>
                                    <div className="b360-order-expand-box">
                                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                                        <h4 style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                                          Order Breakdown: {o.orderId}
                                        </h4>
                                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                                          Source Collection: <code>{o.source}</code>
                                        </span>
                                      </div>

                                      {/* If settled, display RTGS & metal payment info */}
                                      {o.paymentDetails && (
                                        <div style={{ marginBottom: "12px", padding: "10px", background: "#fff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                          <div style={{ fontWeight: 700, fontSize: "12px", color: "#166534", marginBottom: "6px" }}>
                                            Settlement Payment Particulars
                                          </div>
                                          <div className="b360-field-grid">
                                            <div className="b360-field">
                                              <span className="b360-field-label">Total Amount Paid</span>
                                              <span className="b360-field-value">{fmt(o.paymentDetails.totalAmountPaid)}</span>
                                            </div>
                                            <div className="b360-field">
                                              <span className="b360-field-label">Delivered Fine Weight</span>
                                              <span className="b360-field-value">{safeNum(o.paymentDetails.totalFineWtDelivered).toFixed(3)}g</span>
                                            </div>
                                          </div>

                                          {/* Metal Entries */}
                                          {o.paymentDetails.rtgsBucket?.metalEntries?.length > 0 && (
                                            <div style={{ marginTop: "10px" }}>
                                              <span className="b360-field-label">Metal Settlement Entries:</span>
                                              <table className="b360-items-table">
                                                <thead>
                                                  <tr>
                                                    <th>Gross Wt</th>
                                                    <th>Purity</th>
                                                    <th>Fine Wt</th>
                                                    <th>Bill / Ref No</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {o.paymentDetails.rtgsBucket.metalEntries.map((m, mIdx) => (
                                                    <tr key={mIdx}>
                                                      <td>{safeNum(m.grossWt).toFixed(3)}g</td>
                                                      <td>{m.purity}%</td>
                                                      <td>{safeNum(m.fineWt).toFixed(3)}g</td>
                                                      <td>{m.billNo || "N/A"}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Order detail items table */}
                                      {detailEntries.length > 0 && (
                                        <div>
                                          <span className="b360-field-label">Product Line Items:</span>
                                          <table className="b360-items-table">
                                            <thead>
                                              <tr>
                                                <th>Category / Item</th>
                                                <th>Purity</th>
                                                <th>Qty</th>
                                                <th>Gross Wt</th>
                                                <th>Net Wt</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {detailEntries.map((d, dIdx) => (
                                                <tr key={dIdx}>
                                                  <td>{d.category || d.name || `Item #${dIdx + 1}`}</td>
                                                  <td>{d.purity || "—"}</td>
                                                  <td>{d.finalQty ?? d.qty ?? 1}</td>
                                                  <td>{safeNum(d.finalGrossWt ?? d.grossWT).toFixed(3)}g</td>
                                                  <td>{safeNum(d.finalNetWt ?? d.netwt).toFixed(3)}g</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════
            MODAL 1: DOCUMENT PREVIEW MODAL (Full Inspector with Multi-Mode PDF Viewer)
           ══════════════════════════════════════════════════════ */}
        {previewDoc && (
          <div className="b360-modal-overlay" onClick={() => setPreviewDoc(null)}>
            <div
              className={`b360-modal-content ${previewDoc.isPdf ? "b360-pdf-modal" : ""}`}
              style={{ maxWidth: previewDoc.isPdf ? "920px" : "780px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="b360-modal-header">
                <h3 className="b360-modal-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FileText size={16} style={{ color: previewDoc.isPdf ? "#dc2626" : "#8a6b32" }} />
                  <span>{previewDoc.title}</span>
                  {previewDoc.isPdf && (
                    <span className="b360-pdf-badge" style={{ position: "static", fontSize: "10px", padding: "2px 6px" }}>PDF</span>
                  )}
                </h3>
                <button
                  className="b360-modal-close"
                  onClick={() => setPreviewDoc(null)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="b360-modal-body" style={{ padding: 0 }}>
                {previewDoc.isPdf ? (
                  <div className="b360-pdf-modal-wrapper">
                    {/* Top PDF Toolbar with Mode Selector */}
                    <div className="b360-modal-view-mode-bar">
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", fontWeight: 700 }}>
                        <FileText size={15} style={{ color: "#ef4444" }} />
                        <span>PDF Viewer: {previewDoc.title}</span>
                      </div>
                      <div className="b360-mode-btns">
                        <button
                          type="button"
                          className={`b360-mode-btn ${pdfViewMode === "rendered" ? "active" : ""}`}
                          onClick={() => setPdfViewMode("rendered")}
                          title="High-resolution rendered page (works across all browsers with zero errors)"
                        >
                          🖼️ Rendered Document
                        </button>
                        <button
                          type="button"
                          className={`b360-mode-btn ${pdfViewMode === "native" ? "active" : ""}`}
                          onClick={() => setPdfViewMode("native")}
                          title="Native Browser PDF plugin"
                        >
                          📄 Direct PDF
                        </button>
                        <button
                          type="button"
                          className={`b360-mode-btn ${pdfViewMode === "gdocs" ? "active" : ""}`}
                          onClick={() => setPdfViewMode("gdocs")}
                          title="Google Docs Cloud PDF Viewer"
                        >
                          🌐 Google Docs
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <a
                          href={previewDoc.rawUrl || previewDoc.url}
                          target="_blank"
                          rel="noreferrer"
                          className="b360-doc-btn"
                          style={{ color: "#f8fafc", borderColor: "#475569", background: "#1e293b", padding: "5px 12px", fontSize: "12px" }}
                        >
                          <ExternalLink size={12} /> New Tab
                        </a>
                        <a
                          href={previewDoc.downloadUrl || previewDoc.rawUrl || previewDoc.url}
                          download
                          className="b360-doc-btn"
                          style={{ color: "#f8fafc", borderColor: "#475569", background: "#1e293b", padding: "5px 12px", fontSize: "12px" }}
                        >
                          <Download size={12} /> Download
                        </a>
                      </div>
                    </div>

                    {/* PDF viewer display */}
                    <div
                      className="b360-preview-container is-pdf"
                      style={{
                        minHeight: "480px",
                        maxHeight: "620px",
                        position: "relative",
                        overflowY: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background: "#334155",
                        padding: "16px",
                      }}
                    >
                      {pdfViewMode === "rendered" ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                          <img
                            src={previewDoc.renderUrl || previewDoc.url}
                            alt={previewDoc.title}
                            className="b360-pdf-render-img"
                            onError={() => {
                              // If render transform fails, fallback automatically to native iframe
                              setPdfViewMode("native");
                            }}
                          />
                        </div>
                      ) : pdfViewMode === "native" ? (
                        <iframe
                          src={previewDoc.url}
                          title={previewDoc.title}
                          className="b360-preview-iframe"
                          style={{ width: "100%", height: "520px", border: "none", background: "#fff", borderRadius: "6px" }}
                        />
                      ) : (
                        <iframe
                          src={getPdfEmbedUrl(previewDoc.url)}
                          title={previewDoc.title}
                          className="b360-preview-iframe"
                          style={{ width: "100%", height: "520px", border: "none", background: "#fff", borderRadius: "6px" }}
                        />
                      )}
                    </div>

                    {/* Quick navigation and information bar */}
                    <div style={{ background: "#f8fafc", padding: "10px 18px", borderTop: "1px solid #e2e8f0", fontSize: "12px", color: "#64748b", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>
                        Showing: <strong>{pdfViewMode === "rendered" ? "Rendered Document View" : pdfViewMode === "native" ? "Direct PDF Viewer" : "Google Docs Viewer"}</strong>
                      </span>
                      <a
                        href={previewDoc.rawUrl || previewDoc.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "#8a6b32", fontWeight: 700 }}
                      >
                        Direct PDF Link →
                      </a>
                    </div>
                  </div>
                ) : previewDoc.isImg ? (
                  <div className="b360-preview-container" style={{ padding: "20px", display: "flex", justifyContent: "center" }}>
                    <img
                      src={previewDoc.url}
                      alt={previewDoc.title}
                      className="b360-preview-img"
                    />
                  </div>
                ) : (
                  <div className="b360-preview-container" style={{ height: "480px" }}>
                    <iframe
                      src={previewDoc.url}
                      title={previewDoc.title}
                      className="b360-preview-iframe"
                      style={{ width: "100%", height: "100%", border: "none" }}
                    />
                  </div>
                )}

                <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", borderTop: "1px solid #f1f5f9" }}>
                  <div>
                    <span className="b360-field-label">Category:</span>
                    <div style={{ fontWeight: 600 }}>{previewDoc.category}</div>
                    {previewDoc.docId && (
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "3px", fontFamily: "monospace" }}>
                        ID: {previewDoc.docId}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="b360-field-label">Verification Status:</span>
                    <div>
                      <span className={`b360-doc-badge ${previewDoc.status}`}>
                        {previewDoc.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="b360-modal-footer">
                <a
                  href={previewDoc.rawUrl || previewDoc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="b360-doc-btn"
                >
                  <ExternalLink size={13} /> Open Full Screen
                </a>
                <a
                  href={previewDoc.downloadUrl || previewDoc.rawUrl || previewDoc.url}
                  download
                  className="b360-doc-btn"
                >
                  <Download size={13} /> Download
                </a>

                <button
                  className="b360-doc-btn"
                  style={{ color: "#dc2626", borderColor: "#fca5a5" }}
                  onClick={() => handleVerifyDocument(previewDoc.id, "rejected")}
                >
                  <Ban size={13} /> Reject Document
                </button>

                <button
                  className="b360-doc-btn primary"
                  onClick={() => handleVerifyDocument(previewDoc.id, "verified")}
                >
                  <Check size={13} /> Approve & Verify
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════
            MODAL 2: CHANGE STATUS MODAL
           ══════════════════════════════════════════════════ */}
        {showStatusModal && (
          <div className="b360-modal-overlay" onClick={() => setShowStatusModal(false)}>
            <div
              className="b360-modal-content"
              style={{ maxWidth: "480px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="b360-modal-header">
                <h3 className="b360-modal-title">Change Account Status</h3>
                <button
                  className="b360-modal-close"
                  onClick={() => setShowStatusModal(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="b360-modal-body">
                <div className="b360-form-group">
                  <label className="b360-form-label">New Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="b360-form-select"
                  >
                    <option value="active">Active (Full Access)</option>
                    <option value="verified">Verified (Compliant)</option>
                    <option value="pending">Pending KYC Review</option>
                    <option value="suspended">Suspended (Temporary Freeze)</option>
                    <option value="blocked">Blocked (Access Revoked)</option>
                  </select>
                </div>

                <div className="b360-form-group">
                  <label className="b360-form-label">Audit Reason / Note</label>
                  <textarea
                    rows={3}
                    placeholder="Enter reason for status change..."
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    className="b360-form-textarea"
                  />
                </div>
              </div>

              <div className="b360-modal-footer">
                <button
                  className="b360-doc-btn"
                  onClick={() => setShowStatusModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="b360-doc-btn primary"
                  onClick={handleUpdateStatus}
                  disabled={statusUpdating}
                >
                  {statusUpdating ? "Updating..." : "Save Status"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Buyer360;
